import * as React from 'react';
import { Page, Pane, PaneBackButton, PaneBody, PaneHeader } from '../../atoms/applications/page';
import { ApplicationPageState } from '../../../containers/application/states/ApplicationPageState';
import { inject, observer } from 'mobx-react';
import { PlanSummary } from '../../molecules/applications/PlanSummary';
import { EntranceFeeSummary } from '../../molecules/applications/EntranceFeeSummary';
import { ButtonWrapper, SubmitButton } from '../../atoms/forms/vertical/base';
import { CardSummary } from '../../molecules/payment/CardInput';
import './confirmationPane.module.scss';
import { SummaryBody, SummaryHeader, SummaryPriceRow, SummarySubPriceRow } from '../../molecules/applications/base';
import { ApplicationErrorSummary } from '../../molecules/applications/ApplicationErrorSummary';
import { CampaignType, V2MemberCampaignSpec } from '../../../types/membershipPlans';
import { V2MigrationNotice } from '../../molecules/applications/V2MigrationNotice';
import { ServiceAgreementPane } from '../service_agreements/ServiceAgreementPane';
import { observable } from 'mobx';
import { UserAddressSummary } from '../../../components/molecules/applications/UserAddressForm';

export const SummaryWithEntranceFee = ({ selectedPlan, selectedItem, entranceFee, entranceInstallments }) => {
    const option = entranceFee.options.find((o) => o.installments === entranceInstallments);
    return (
        <>
            <PlanSummary plan={selectedPlan} item={selectedItem} />
            <EntranceFeeSummary entranceFee={entranceFee} installments={entranceInstallments} />
            <SummaryBody background="white">
                {entranceInstallments > 1 && (
                    <>
                        <SummarySubPriceRow
                            label="合計"
                            price={selectedItem.total_price + option.total_price}
                            unit="円(税込)"
                        />
                        <SummaryPriceRow
                            label="初回お支払い額の合計"
                            price={selectedItem.total_price + option.monthly_payments[0]}
                            unit={'円(税込)'}
                        />
                    </>
                )}
                {entranceInstallments === 1 && (
                    <SummaryPriceRow
                        label="お支払い額"
                        price={selectedItem.total_price + option.monthly_payments[0]}
                        unit={'円(税込)'}
                    />
                )}
            </SummaryBody>
        </>
    );
};

// eslint-disable-next-line no-unused-vars
export const V2DiscountSummary = ({ selectedPlan, selectedItem, freeTrialMonths, paymentStartsAt }) => (
    <>
        <SummaryHeader>新プラン移行割引の適用</SummaryHeader>
        <SummaryBody>
            <SummarySubPriceRow label={`${selectedPlan.name} - ${freeTrialMonths}ヶ月分`} price={0} unit="円" />
        </SummaryBody>
        <SummaryBody background="white">
            <SummarySubPriceRow label="合計" price={0} unit="円(税込)" />
            <SummaryPriceRow label="初回お支払額の合計" price={0} unit="円(税込)" />
        </SummaryBody>
        <V2MigrationNotice freeTrialMonths={freeTrialMonths} paymentStartsAt={paymentStartsAt} />
    </>
);

export const SummaryWithoutEntrance = ({ selectedPlan, selectedItem }) => {
    return (
        <>
            <PlanSummary plan={selectedPlan} item={selectedItem} />
        </>
    );
};

export const SummaryPane = ({ selectedPlan, selectedItem, entranceFee, entranceInstallments }) => {
    if (entranceFee) {
        return (
            <SummaryWithEntranceFee
                selectedPlan={selectedPlan}
                selectedItem={selectedItem}
                entranceFee={entranceFee}
                entranceInstallments={entranceInstallments}
            />
        );
    }
    const v2Campaign = selectedPlan.discount_campaigns.find(
        (c) => c.type === CampaignType.V2_MEMBER
    ) as V2MemberCampaignSpec;
    if (v2Campaign) {
        return (
            <V2DiscountSummary
                selectedPlan={selectedPlan}
                selectedItem={selectedItem}
                freeTrialMonths={v2Campaign.free_trial_months}
                paymentStartsAt={v2Campaign.payment_starts_at}
            />
        );
    }
    return <SummaryWithoutEntrance selectedPlan={selectedPlan} selectedItem={selectedItem} />;
};

const LegacyServiceAgreementPane = observer(({ agreed, onAgreeStatusChange }) => {
    return (
        <div styleName="agreement">
            <input id="agreed" type="checkbox" checked={agreed} onChange={onAgreeStatusChange} />
            <label htmlFor="agreed">
                <a href="https://she-inc.jp/common/agreement.html" target="_blank">
                    利用規約
                </a>に同意して申し込む
            </label>
        </div>
    );
});

const ServiceAgreementPaneSwitch = observer(({ serviceAgreementState, agreedOfLegacy, onCheckOfLegacy }) => {
    if (serviceAgreementState.serviceAgreement) {
        return <ServiceAgreementPane paneState={serviceAgreementState} />;
    } else {
        return <LegacyServiceAgreementPane agreed={agreedOfLegacy} onAgreeStatusChange={onCheckOfLegacy} />;
    }
});

type Props = {
    applicationState: ApplicationPageState;
};

@inject('applicationState')
@observer
export class ConfirmationPane extends React.Component<Props> {
    @observable agreed = false;

    onCheck(checked: boolean) {
        this.agreed = checked;
    }

    render() {
        const {
            applicationState: {
                error,
                submitting,
                canGoBack,
                addressFormState,
                cardState,
                selectedPlan,
                selectedItem,
                entranceFee,
                entranceInstallments,
                serviceAgreementState,
                submit,
            },
        } = this.props;
        if (!selectedItem || !selectedPlan || !cardState.card) {
            return null;
        }
        const showNewAgreement = !!serviceAgreementState.serviceAgreement;
        const allAgreed = showNewAgreement ? serviceAgreementState.isAllAgreed : this.agreed;
        return (
            <Page>
                <Pane>
                    {canGoBack && <PaneBackButton />}
                    <PaneHeader>お申し込み内容の確認</PaneHeader>
                    <PaneBody>
                        <SummaryHeader>住所</SummaryHeader>
                        <SummaryBody>
                            <UserAddressSummary userAddress={addressFormState.getDirtyValue()} />
                        </SummaryBody>
                        <SummaryHeader>お支払い方法</SummaryHeader>
                        <SummaryBody>
                            <CardSummary styleName="card-summary" card={cardState.card} />
                        </SummaryBody>
                        <SummaryPane
                            selectedPlan={selectedPlan}
                            selectedItem={selectedItem}
                            entranceFee={entranceFee}
                            entranceInstallments={entranceInstallments}
                        />
                        <ServiceAgreementPaneSwitch
                            serviceAgreementState={serviceAgreementState}
                            agreedOfLegacy={this.agreed}
                            onCheckOfLegacy={() => this.onCheck(!this.agreed)}
                        />
                        {error && (
                            <ApplicationErrorSummary title="お申し込みを確定することができませんでした。" type="normal">
                                考えられる要因<br />
                                ・クレジットカードの限度額<br />
                                ・引き落とし先の銀行口座の残高不足<br />
                                など
                            </ApplicationErrorSummary>
                        )}
                        <ButtonWrapper>
                            <SubmitButton
                                id="application-submit"
                                loading={submitting}
                                disabled={!allAgreed}
                                onClick={submit}
                            >
                                お申し込みを確定する
                            </SubmitButton>
                        </ButtonWrapper>
                    </PaneBody>
                </Pane>
            </Page>
        );
    }
}
